import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Input,
  Field,
  FieldLabel,
} from '../../components/ui';
import { useAuthStore } from '../../store/auth';
import { useCurrentAcademy } from '../../hooks/useDataStore';
import { hasRole, TierFeatureKey } from '@court-sync/shared';
import { useCoachAcademyFeatureAccess } from '@/hooks/useFeatureAccess';
import { useSubscriptionCheck } from '../../hooks/useSubscriptionCheck';
import {
  academyAdminService,
  type AcademyAdminRow,
  type AcademyAdminLimitInfo,
} from '../../services/academyAdmin';
import { Shield, CheckCircle2, Clock, XCircle } from 'lucide-react';

type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

function InviteStatusBadge({ status }: { status?: InviteStatus }) {
  if (!status) return null;
  const config = {
    pending: { icon: Clock, label: 'Invite sent', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
    accepted: { icon: CheckCircle2, label: 'Accepted', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    expired: { icon: XCircle, label: 'Invite expired', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
    revoked: { icon: XCircle, label: 'Revoked', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  };
  const { icon: Icon, label, className } = config[status] ?? config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

/**
 * Academy owner only; gated by Professional+ (ACADEMY_ADMINS). Rendered from Settings.
 */
export default function SettingsAdministratorsTab() {
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const navigate = useNavigate();
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { hasAccess: canUseAcademyAdmins, tierResolved } = useCoachAcademyFeatureAccess(
    currentAcademy,
    currentAcademy?.slug ?? null,
    TierFeatureKey.ACADEMY_ADMINS
  );

  const [adminRows, setAdminRows] = useState<AcademyAdminRow[]>([]);
  const [adminLimit, setAdminLimit] = useState<AcademyAdminLimitInfo | null>(null);
  const [adminInviteStatus, setAdminInviteStatus] = useState<Record<string, InviteStatus>>({});
  const [adminEmailInput, setAdminEmailInput] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);
  const [sendingAdminInvite, setSendingAdminInvite] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!hasRole(user, 'academy_owner')) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!currentAcademy?.id) return;
    if (!tierResolved) return;
    if (!canUseAcademyAdmins) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [currentAcademy?.id, canUseAcademyAdmins, tierResolved, navigate]);

  const refreshAdmins = useCallback(async () => {
    if (!currentAcademy?.id || !tierResolved || !canUseAcademyAdmins) return;
    setAdminLoading(true);
    try {
      const [listRes, statusRes] = await Promise.all([
        academyAdminService.listAdmins(currentAcademy.id),
        academyAdminService.getInviteStatus(currentAcademy.id),
      ]);
      setAdminRows(listRes.admins);
      setAdminLimit(listRes.limit);
      setAdminInviteStatus(statusRes);
    } catch {
      toast.error('Failed to load administrators');
    } finally {
      setAdminLoading(false);
    }
  }, [currentAcademy?.id, tierResolved, canUseAcademyAdmins]);

  useEffect(() => {
    void refreshAdmins();
  }, [refreshAdmins]);

  const handleSendAdminInvite = async () => {
    if (!currentAcademy?.id || !adminEmailInput.trim()) return;
    try {
      setSendingAdminInvite(true);
      await academyAdminService.sendInvite(currentAcademy.id, adminEmailInput.trim());
      toast.success('Invitation sent');
      setAdminEmailInput('');
      await refreshAdmins();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to send invitation');
    } finally {
      setSendingAdminInvite(false);
    }
  };

  const handleRemoveAdmin = async (row: AcademyAdminRow) => {
    if (!currentAcademy?.id) return;
    if (!confirm(`Remove ${row.email} as an academy administrator?`)) return;
    try {
      await academyAdminService.removeAdmin(currentAcademy.id, row.userId);
      toast.success('Administrator removed');
      await refreshAdmins();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to remove administrator');
    }
  };

  if (!user || !hasRole(user, 'academy_owner')) {
    return null;
  }

  if (!currentAcademy) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" aria-label="Loading" />
        </CardContent>
      </Card>
    );
  }

  if (!tierResolved) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!canUseAcademyAdmins) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <Shield className="h-6 w-6 text-indigo-600 shrink-0 mt-0.5" aria-hidden />
          <div>
            <h2 className="text-lg font-semibold">Academy administrators</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Invite trusted people to help manage your academy. They cannot access payments or invite other administrators.
              Pending invitations count toward your plan&apos;s administrator limit.
            </p>
            {adminLimit && (
              <p className="text-sm text-muted-foreground mt-2">
                {adminLimit.maxAdmins === -1
                  ? `${adminLimit.slotsUsed} administrator${adminLimit.slotsUsed === 1 ? '' : 's'} (unlimited on your plan)`
                  : `${adminLimit.slotsUsed} of ${adminLimit.maxAdmins} slots used (includes pending invites)`}
              </p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <Field className="flex-1">
            <FieldLabel htmlFor="settings-admin-invite-email">Email address</FieldLabel>
            <Input
              id="settings-admin-invite-email"
              type="email"
              placeholder="colleague@example.com"
              value={adminEmailInput}
              onChange={(e) => setAdminEmailInput(e.target.value)}
              disabled={adminLoading || sendingAdminInvite}
            />
          </Field>
          <Button
            type="button"
            title={!hasValidSubscription ? tooltipMessage : undefined}
            onClick={() => void handleSendAdminInvite()}
            disabled={
              sendingAdminInvite ||
              !adminEmailInput.trim() ||
              !hasValidSubscription ||
              (adminLimit != null &&
                adminLimit.maxAdmins !== -1 &&
                adminLimit.slotsUsed >= adminLimit.maxAdmins)
            }
          >
            {sendingAdminInvite ? 'Sending…' : 'Send invite'}
          </Button>
        </div>

        {adminLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : adminRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No additional administrators yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
            {adminRows.map((row) => (
              <li key={row.userId} className="flex items-center justify-between gap-4 px-4 py-3">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {row.firstName} {row.lastName}
                  </p>
                  <p className="text-sm text-muted-foreground">{row.email}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-red-600"
                  onClick={() => void handleRemoveAdmin(row)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        {Object.keys(adminInviteStatus).length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pending by email</p>
            <ul className="text-sm space-y-1">
              {Object.entries(adminInviteStatus).map(([email, st]) => (
                <li key={email} className="flex justify-between gap-4">
                  <span>{email}</span>
                  <InviteStatusBadge status={st} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
